import { yupResolver } from '@hookform/resolvers/yup'
import { useFormFactory } from '@meilleurtaux/form-factory'
import {
  ButtonValidateSaveForLater,
  Col,
  Field,
  Modal,
  Row,
  useDeviceDetection,
  useTracking,
} from '@meilleurtaux/form-factory-ds'
import { useRef, useState, type FormEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import * as yup from 'yup'

const FieldWrapper = styled.div`
  label {
    font-size: 14px;
  }

  @media (min-width: 1024px) {
    /* 1024px aligne sur SCREEN_M du design system mass-node-dsformfactory */
    label {
      font-size: 16px;
    }
  }
`

const emailPattern = /[^@\s]+@[^@\s]+\.[^@\s]+/
interface SaveAndContinueLaterProps {
  open: boolean
  onClose: () => void
}

type EmailSend = 'none' | 'loading' | 'success' | 'error'

export default function SaveAndContinueLater({
  open,
  onClose,
}: Readonly<SaveAndContinueLaterProps>) {
  const { userEmail, saveForLater } = useFormFactory(import.meta.env.VITE_API_URL)
  const [isEmailSent, setIsEmailSent] = useState<EmailSend>('none')
  const { trackModalClose } = useTracking()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const device = useDeviceDetection()
  const isMobile = device === 'Mobile'

  const handleClose = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    trackModalClose({
      title: 'Fermer Sauvegardez votre progression',
      type: 'save_later',
    })
    onClose()
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(
      yup.object({
        Email: yup
          .string()
          .required('Veuillez indiquer votre adresse email')
          .matches(emailPattern, 'Veuillez indiquer une adresse email valide'),
      }),
    ),
    defaultValues: { Email: userEmail },
  })

  const handleSubmitEmail = (ev: FormEvent) => {
    setIsEmailSent('loading')
    ev.preventDefault()
    if (isValid) {
      handleSubmit(({ Email }) => {
        if (Email) {
          saveForLater(Email)
            .then(() => {
              setIsEmailSent('success')
              timeoutRef.current = setTimeout(handleClose, 3000)
            })
            .catch(() => setIsEmailSent('error'))
        } else {
          setIsEmailSent('error')
        }
      })()
    } else {
      setIsEmailSent('error')
    }
  }

  if (isEmailSent === 'success') {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        size="l"
        aria-labelledby="save-modal-title"
        style={{ paddingBottom: isMobile ? 32 : 48 }}
        closeIconFill="#868686"
      >
        <Row gap={16}>
          <Col gap={0}>
            <h2
              id="save-modal-title"
              style={{
                fontSize: isMobile ? 16 : 24,
                fontWeight: 700,
                lineHeight: '39px',
                margin: '0',
                textAlign: 'center',
              }}
            >
              Votre parcours a été sauvegardé avec succès!
            </h2>
          </Col>
        </Row>
        <Row gap={16}>
          <Col gap={0}>
            <Row>
              <Col col={12} gap={0}>
                <span
                  style={{
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 400,
                    lineHeight: '24px',
                    margin: '0 0 20px 0',
                  }}
                >
                  Reprenez-le à tout moment en cliquant sur le lien envoyé par email.
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="l"
      aria-labelledby="save-modal-title"
      style={{ paddingBottom: isMobile ? 32 : 48 }}
      closeIconFill="#868686"
    >
      <span
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {isEmailSent === 'loading' && 'Envoi en cours...'}
        {isEmailSent === 'error' && 'Une erreur est survenue. Veuillez réessayer.'}
      </span>
      <form onSubmit={handleSubmitEmail}>
        <Row gap={16}>
          <Col gap={16}>
            <h2
              id="save-modal-title"
              style={{
                fontSize: isMobile ? 16 : 24,
                fontWeight: 700,
                lineHeight: '39px',
                margin: '0',
                textAlign: 'center',
              }}
            >
              Sauvegardez votre progression
            </h2>
          </Col>
        </Row>
        <Row gap={16}>
          <Col gap={0}>
            <Row>
              <Col col={12} gap={8}>
                <span
                  style={{
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 400,
                    lineHeight: '24px',
                    margin: '0 0 20px 0',
                  }}
                >
                  Continuez votre comparaison à tout moment grâce au lien qui vous sera envoyé par
                  email :
                </span>
              </Col>
            </Row>
            <Row alignItems="flex-end">
              <Col col={12} m={7} gap={8}>
                <FieldWrapper>
                  <Controller
                    name="Email"
                    control={control}
                    render={({ field }) => (
                      <Field
                        type="email"
                        extraInfo=""
                        label="Email"
                        status={errors.Email?.message === undefined ? 'default' : 'error'}
                        message={errors.Email?.message}
                        template="default"
                        lastField={false}
                        {...field}
                      />
                    )}
                  />
                </FieldWrapper>
              </Col>
              <Col col={12} m={5} gap={8}>
                <ButtonValidateSaveForLater isEmailSent={isEmailSent} />
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </Modal>
  )
}
